import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Phone, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Send,
  Shield,
  Clock,
  Crown,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      toast.error('نام کاربری الزامی است');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('شماره موبایل الزامی است');
      return false;
    }
    if (formData.phone.length !== 11 || !formData.phone.startsWith('09')) {
      toast.error('شماره موبایل معتبر نیست');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('رمز عبور و تکرار آن یکسان نیستند');
      return false;
    }
    return true;
  };

  const sendOTP = async () => {
    if (!formData.phone.trim()) {
      toast.error('ابتدا شماره موبایل را وارد کنید');
      return;
    }
    if (formData.phone.length !== 11 || !formData.phone.startsWith('09')) {
      toast.error('شماره موبایل معتبر نیست');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Sending OTP to:', formData.phone);
      
      const response = await axios.post('/api/auth/send-otp', {
        phone: formData.phone
      });
      
      console.log('Send OTP response:', response.data);
      
      if (response.data.success) {
        setOtpSent(true);
        setCountdown(120); // 2 minutes countdown
        toast.success('کد تأیید ارسال شد');
        
        // Start countdown
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error(response.data.message || 'خطا در ارسال کد تأیید');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'خطا در ارسال کد تأیید');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode.trim() || otpCode.length !== 4) {
      toast.error('کد تأیید 4 رقمی را وارد کنید');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Verifying OTP:', { phone: formData.phone, otp: otpCode });
      
      const response = await axios.post('/api/auth/verify-otp', {
        phone: formData.phone,
        otp: otpCode
      });
      
      console.log('OTP verification response:', response.data);
      
      if (response.data.success) {
        setOtpVerified(true);
        toast.success('شماره موبایل تأیید شد');
      } else {
        toast.error(response.data.message || 'کد تأیید اشتباه است');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'کد تأیید اشتباه است');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!otpVerified) {
      toast.error('ابتدا شماره موبایل خود را تأیید کنید');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Sending registration data:', {
        username: formData.username,
        phone: formData.phone,
        password: formData.password
      });

      // Test if server is reachable
      try {
        await axios.get('/api/auth/test');
        console.log('Server is reachable');
      } catch (testError) {
        console.log('Server test failed:', testError.message);
      }

      const response = await axios.post('/api/auth/register', {
        username: formData.username,
        phone: formData.phone,
        password: formData.password
      });
      
      console.log('Registration response:', response.data);
      
      if (response.data.success) {
        toast.success('ثبت‌نام با موفقیت انجام شد');
        navigate('/login');
      } else {
        toast.error(response.data.message || 'خطا در ثبت‌نام');
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      if (error.code === 'ERR_NETWORK') {
        toast.error('خطا در اتصال به سرور. لطفاً اینترنت خود را بررسی کنید.');
      } else if (error.response?.status === 404) {
        toast.error('API endpoint یافت نشد. لطفاً با مدیر تماس بگیرید.');
      } else if (error.response?.status === 500) {
        toast.error('خطای سرور. لطفاً بعداً تلاش کنید.');
      } else {
        toast.error(error.response?.data?.message || 'خطا در ثبت‌نام');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 dark:bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500/10 dark:bg-indigo-500/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="max-w-md mx-auto relative z-10">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            بازگشت به صفحه اصلی
          </Link>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 dark:border-gray-700/30"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <motion.div variants={itemVariants} className="relative z-10">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
              >
                <img src="/logo.jpg" alt="AvalonMC Logo" className="w-16 h-16 object-cover rounded-xl" />
              </motion.div>
              <motion.h1 
                variants={itemVariants}
                className="text-3xl font-bold mb-2"
              >
                ثبت‌نام در آوالون ام‌سی
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-blue-100 text-lg"
              >
                حساب کاربری جدید ایجاد کنید
              </motion.p>
            </motion.div>
          </div>

          {/* Form */}
          <div className="p-8">
            <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-6">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  نام کاربری
                </label>
                <div className="relative group">
                  <User className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 group-focus-within:text-blue-600 transition-colors" />
                                     <input
                     type="text"
                     name="username"
                     value={formData.username}
                     onChange={handleInputChange}
                     className="w-full pr-12 py-3 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-xl transition-all duration-200"
                     placeholder="نام کاربری خود را وارد کنید"
                   />
                </div>
              </div>

              

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  شماره موبایل
                </label>
                <div className="relative group">
                  <Phone className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 group-focus-within:text-blue-600 transition-colors" />
                                     <input
                     type="tel"
                     name="phone"
                     value={formData.phone}
                     onChange={handleInputChange}
                     className="w-full pr-12 py-3 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-xl transition-all duration-200"
                     placeholder="09xxxxxxxxx"
                     maxLength="11"
                   />
                </div>
                
                {/* Send OTP Button */}
                <motion.button
                  variants={itemVariants}
                  type="button"
                  onClick={sendOTP}
                  disabled={isLoading || countdown > 0}
                  className="mt-3 w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : countdown > 0 ? (
                    <>
                      <Clock className="w-4 h-4" />
                      ارسال مجدد ({formatCountdown(countdown)})
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      ارسال کد تأیید
                    </>
                  )}
                </motion.button>
              </div>

              {/* OTP Verification */}
              {otpSent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200 dark:border-blue-800"
                >
                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                    <Shield className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">تأیید شماره موبایل</span>
                  </div>
                  
                  <div className="flex space-x-2 space-x-reverse">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      className="flex-1 text-center py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-lg font-mono bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm"
                      placeholder="0000"
                      maxLength="4"
                    />
                    <button
                      type="button"
                      onClick={verifyOTP}
                      disabled={isLoading || otpVerified}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-xl border-2 border-green-500 shadow-lg"
                      style={{ minWidth: '80px' }}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : otpVerified ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        'تأیید'
                      )}
                    </button>
                  </div>
                  
                  {otpVerified && (
                    <div className="flex items-center text-green-600 dark:text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      شماره موبایل تأیید شد
                    </div>
                  )}
                </motion.div>
              )}

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  رمز عبور
                </label>
                <div className="relative group">
                  <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 group-focus-within:text-blue-600 transition-colors" />
                                     <input
                     type={showPassword ? 'text' : 'password'}
                     name="password"
                     value={formData.password}
                     onChange={handleInputChange}
                     className="w-full pr-12 pl-12 py-3 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-xl transition-all duration-200"
                     placeholder="رمز عبور خود را وارد کنید"
                   />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  تکرار رمز عبور
                </label>
                <div className="relative group">
                  <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 group-focus-within:text-blue-600 transition-colors" />
                                     <input
                     type={showConfirmPassword ? 'text' : 'password'}
                     name="confirmPassword"
                     value={formData.confirmPassword}
                     onChange={handleInputChange}
                     className="w-full pr-12 pl-12 py-3 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-xl transition-all duration-200"
                     placeholder="رمز عبور را تکرار کنید"
                   />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
                             <motion.button
                 variants={itemVariants}
                 type="submit"
                 disabled={isLoading || !otpVerified}
                 className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center gap-3 group"
               >
                {isLoading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>در حال ثبت‌نام...</span>
                  </div>
                ) : (
                  <>
                    <span>ثبت‌نام</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </motion.form>

            {/* Login Link */}
            <motion.div 
              variants={itemVariants}
              className="mt-8 text-center"
            >
              <p className="text-gray-600 dark:text-gray-300">
                قبلاً حساب کاربری دارید؟{' '}
                <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors hover:underline">
                  ورود به حساب
                </Link>
              </p>
            </motion.div>

            {/* Features */}
            <motion.div 
              variants={itemVariants}
              className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl">
                  <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-700 dark:text-gray-200">امنیت بالا</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl">
                  <Crown className="w-8 h-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-700 dark:text-gray-200">دسترسی ویژه</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
